import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { KeyRound, LoaderCircle, ShieldAlert } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  ADMIN_SESSION_INVALID_EVENT,
  ADMIN_SESSION_STORAGE_KEY,
  readApiJson
} from '../lib/api';

const AdminPinGate = ({ children }) => {
  const { isAdmin, profileLoading } = useAuth();
  const [pin, setPin] = useState('');
  const [status, setStatus] = useState('checking');
  const [error, setError] = useState('');
  const [expiresAt, setExpiresAt] = useState(null);

  useEffect(() => {
    let active = true;
    if (profileLoading) return () => { active = false; };
    if (!isAdmin) {
      setStatus('denied');
      return () => { active = false; };
    }
    if (!localStorage.getItem(ADMIN_SESSION_STORAGE_KEY)) {
      setStatus('locked');
      return () => { active = false; };
    }
    readApiJson('/admin/session')
      .then((data) => {
        if (!active) return;
        setExpiresAt(Date.now() + Number(data?.expires_in || 0) * 1000);
        setStatus('unlocked');
      })
      .catch(() => { if (active) setStatus('locked'); });
    return () => { active = false; };
  }, [isAdmin, profileLoading]);

  useEffect(() => {
    if (status !== 'unlocked' || !expiresAt) return undefined;
    const timer = window.setTimeout(() => {
      localStorage.removeItem(ADMIN_SESSION_STORAGE_KEY);
      setStatus('locked');
      setExpiresAt(null);
    }, Math.max(0, expiresAt - Date.now()));
    return () => window.clearTimeout(timer);
  }, [expiresAt, status]);

  useEffect(() => {
    const lock = () => {
      setStatus('locked');
      setExpiresAt(null);
    };
    window.addEventListener(ADMIN_SESSION_INVALID_EVENT, lock);
    return () => window.removeEventListener(ADMIN_SESSION_INVALID_EVENT, lock);
  }, []);

  const unlock = async (event) => {
    event.preventDefault();
    if (!/^\d{6,12}$/.test(pin)) {
      setError('Enter the 6–12 digit administrator PIN.');
      return;
    }
    setStatus('unlocking');
    setError('');
    try {
      const data = await readApiJson('/admin/unlock', {
        method: 'POST',
        body: JSON.stringify({ pin })
      });
      localStorage.setItem(ADMIN_SESSION_STORAGE_KEY, data.admin_session);
      setExpiresAt(Date.now() + Number(data?.expires_in || 0) * 1000);
      setPin('');
      setStatus('unlocked');
    } catch (requestError) {
      setError(requestError.message || 'Administrator access could not be unlocked.');
      setStatus('locked');
    }
  };

  if (status === 'unlocked') return children;

  if (status === 'checking' || profileLoading) {
    return (
      <main className="min-h-screen bg-[#050d18] px-4 pt-28 text-white">
        <div className="mx-auto flex max-w-md items-center justify-center gap-3 rounded-xl border border-[#203950] bg-[#091522] p-8 text-[#b9cbdd]">
          <LoaderCircle className="h-5 w-5 animate-spin" /> Verifying administrator access…
        </div>
      </main>
    );
  }

  if (status === 'denied') {
    return (
      <main className="min-h-screen bg-[#050d18] px-4 pt-28 text-white">
        <div className="mx-auto max-w-md rounded-xl border border-red-400/30 bg-[#091522] p-8 text-center">
          <ShieldAlert className="mx-auto mb-4 h-8 w-8 text-red-300" />
          <h1 className="text-xl font-semibold">Administrator access required</h1>
          <p className="mt-2 text-sm text-[#9fb5c9]">This account is not authorized for the administration area.</p>
          <Link className="mt-5 inline-block text-sm text-[#68d9ff]" to="/">Return to dashboard</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#050d18] px-4 pt-28 text-white">
      <form className="mx-auto max-w-md rounded-xl border border-[#203950] bg-[#091522] p-8" onSubmit={unlock}>
        <KeyRound className="mb-4 h-7 w-7 text-[#68d9ff]" />
        <h1 className="text-xl font-semibold">Unlock administration</h1>
        <p className="mt-2 text-sm text-[#9fb5c9]">Enter the administrator PIN. This browser then stays unlocked for 24 hours, or until you sign out.</p>
        <label className="mt-6 block text-xs uppercase tracking-[0.18em] text-[#9fb5c9]" htmlFor="admin-pin">Administrator PIN</label>
        <input
          id="admin-pin"
          className="mt-2 w-full rounded border border-[#28435d] bg-[#06111d] px-4 py-3 font-mono tracking-[0.35em] outline-none focus:border-[#68d9ff]"
          type="password"
          inputMode="numeric"
          autoComplete="one-time-code"
          minLength={6}
          maxLength={12}
          pattern="[0-9]*"
          value={pin}
          onChange={(event) => setPin(event.target.value.replace(/\D/g, '').slice(0, 12))}
          autoFocus
        />
        {error && <p className="mt-3 text-sm text-red-300" role="alert">{error}</p>}
        <button
          className="mt-5 flex w-full items-center justify-center gap-2 rounded bg-[#68d9ff] px-4 py-3 font-semibold text-[#04101b] disabled:opacity-60"
          type="submit"
          disabled={status === 'unlocking'}
        >
          {status === 'unlocking' && <LoaderCircle className="h-4 w-4 animate-spin" />}
          Unlock
        </button>
      </form>
    </main>
  );
};

export default AdminPinGate;
